import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ApiUrl} from '../../shared/models/apiurl.models';
import {AdvFormService} from '../../pages/create-adv/services/adv-form.service';

export interface AccessoryItem {
  id: number;
  secondaryCategory: number;
  mainCategory: number;
  armatureIcon: string;
  ArmatureDesc: string;
}

@Component({
  selector: 'app-multi-select-box',
  templateUrl: './multi-select-box.component.html',
  styleUrls: ['./multi-select-box.component.css'],
  standalone: false
})
export class MultiSelectBoxComponent implements OnInit, OnDestroy {
  private static readonly accessoryCache = new Map<string, AccessoryItem[]>();
  private readonly destroy$ = new Subject<void>();
  private readonly selectedIds = new Set<number>();
  private readonly materialIconPattern = /^[a-z0-9_]+$/;

  list: AccessoryItem[] = [];
  selectedList: AccessoryItem[] = [];
  isLoading = true;
  loadError = false;

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  constructor(
    private http: HttpClient,
    private apiKey: ApiUrl,
    private formService: AdvFormService
  ) {}

  ngOnInit(): void {
    this.loadAccessories();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  isSelected(item: AccessoryItem): boolean {
    return this.selectedIds.has(item.id);
  }

  getIconName(item: AccessoryItem): string {
    const icon = (item.armatureIcon || '').trim().toLowerCase();
    return this.materialIconPattern.test(icon) ? icon : 'category';
  }

  toggleItem(item: AccessoryItem): void {
    if (this.selectedIds.has(item.id)) {
      this.selectedIds.delete(item.id);
      this.selectedList = this.selectedList.filter(selected => selected.id !== item.id);
    } else {
      this.selectedIds.add(item.id);
      this.selectedList = [...this.selectedList, item];
    }
    this.updateFormValue();
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.selectedList = [];
    this.updateFormValue();
  }

  retry(): void {
    this.loadAccessories(true);
  }

  private loadAccessories(force = false): void {
    const namespace = this.apiKey.getNameSpace();
    const cachedItems = MultiSelectBoxComponent.accessoryCache.get(namespace);

    if (!force && cachedItems) {
      this.list = cachedItems;
      this.restoreExistingSelection();
      this.isLoading = false;
      this.loadError = false;
      return;
    }

    this.isLoading = true;
    this.loadError = false;
    this.http.get<any>(this.apiKey.getAccessories(namespace), this.options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const rawItems = Array.isArray(response) ? response : (response?.results || []);
          this.list = this.normalizeUniqueItems(rawItems);
          MultiSelectBoxComponent.accessoryCache.set(namespace, this.list);
          this.restoreExistingSelection();
          this.isLoading = false;
        },
        error: () => {
          this.list = [];
          this.isLoading = false;
          this.loadError = true;
        }
      });
  }

  private normalizeUniqueItems(rawItems: any[]): AccessoryItem[] {
    const uniqueIds = new Set<number>();
    const uniqueLabels = new Set<string>();
    const items: AccessoryItem[] = [];

    for (const rawItem of rawItems) {
      const id = Number(rawItem?.id);
      const description = String(rawItem?.ArmatureDesc || '').replace(/\s+/g, ' ').trim();
      const normalizedLabel = description.toLocaleLowerCase();
      if (!Number.isFinite(id) || !description || uniqueIds.has(id) || uniqueLabels.has(normalizedLabel)) continue;

      uniqueIds.add(id);
      uniqueLabels.add(normalizedLabel);
      items.push({
        id,
        secondaryCategory: Number(rawItem?.SocondryCategory ?? rawItem?.secondaryCategory),
        mainCategory: Number(rawItem?.MainCategory ?? rawItem?.mainCategory),
        armatureIcon: String(rawItem?.ArmatureIcon ?? rawItem?.armatureIcon ?? ''),
        ArmatureDesc: description
      });
    }
    return items;
  }

  private restoreExistingSelection(): void {
    const existingValue = this.formService.getQuestion('armatureList');
    if (!Array.isArray(existingValue)) return;

    const existingIds = new Set(existingValue.map(item => Number(item?.id)));
    this.selectedList = this.list.filter(item => existingIds.has(item.id));
    this.selectedIds.clear();
    this.selectedList.forEach(item => this.selectedIds.add(item.id));
  }

  private updateFormValue(): void {
    if (this.selectedList.length > 0) {
      this.formService.addQuestion('armatureList', this.selectedList);
    } else {
      this.formService.deleteQuestion('armatureList');
    }
  }
}
